export type NextAppointment = {
  /** Whether the soonest available day is today. */
  isToday: boolean;
  /** Whether the soonest available day is tomorrow. */
  isTomorrow: boolean;
  /** Short weekday + date label for the soonest day (e.g. "Wed, Jun 3"). */
  dayLabel: string;
  /** That day's start times, comma-joined (e.g. "12:45 PM, 1:15 PM"). */
  times: string;
};

const timeFmt = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const dayFmt = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});

/**
 * Summarizes a coach's availability for the matched-coach card's "Next
 * appointment" line: the soonest available day plus that day's start times.
 * Returns undefined when there are no upcoming slots.
 */
export function formatNextAppointment(timeslots: string[]): NextAppointment | undefined {
  // Sort ascending rather than trusting the API's order — the card advertises
  // `dates[0]` as the soonest slot, so an out-of-order response (cache layer,
  // parallel merge, edge reorder) would otherwise make it lie.
  const dates = timeslots
    .map((iso) => new Date(iso))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());
  if (dates.length === 0) return undefined;

  const first = dates[0];
  const firstKey = first.toDateString();
  const times = dates
    .filter((date) => date.toDateString() === firstKey)
    .map((date) => timeFmt.format(date))
    .join(', ');

  const todayKey = new Date().toDateString();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  return {
    isToday: firstKey === todayKey,
    isTomorrow: firstKey === tomorrow.toDateString(),
    dayLabel: dayFmt.format(first),
    times,
  };
}
